import { useContext, useEffect, useState } from "react";
import { ModalTitle } from "@kaltura/ds-react-components";
import { StyledModal, StyledModalContent } from "./StyledComponents";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { EmbedCodeProps, EmbedSettings, PlayerConfig } from "./embed-settings/types";
import { EmbedSettingsAvailableFeatures } from "./embed-settings/embed-helpers/EmbedHelperV7";
import ShareSection, { LinksType } from "./share-section/ShareSection";
import SettingsContainer from "./embed-settings/SettingsContainer";
import { Analytics, ButtonClickAnalyticsType, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { CustomComponent } from "@kaltura/mediaspace-shared-ui";

export interface CustomShareItem {
    icon: KmsTypeComponent;
    tooltip: string;
    component: KmsTypeComponent<any, { onClose: () => void }>;
}

export interface ShareProps {
    title?: string;
    embedSettings?: EmbedSettings[];
    availableFeatures?: EmbedSettingsAvailableFeatures;
    embedCodeParams?: EmbedCodeProps;
    playerConfig?: PlayerConfig;
    hideEmbed?: boolean;
    oEmbed?: boolean;
    allowOembedConfig?: boolean;
    isHeroAction?: boolean;
    entryUrl: string;
    size?: "medium" | "large";
    isTimingAllowed: boolean;
    modalID?: string;
    links: LinksType;
    playlistId?: string | null;
    analyticsData?: Analytics;
    customShareItems?: CustomShareItem[];
}

export interface ShareModalProps extends ShareProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Share Component Modal
 */
export const ShareModal = (props: ShareModalProps) => {
    const {
        title = translate("Share"),
        open,
        onClose,
        embedSettings: propEmbedSettings,
        availableFeatures: propAvailableFeatures,
        embedCodeParams: propEmbedCodeParams,
        playerConfig: propPlayerConfig,
        hideEmbed = false,
        oEmbed = false,
        allowOembedConfig = false,
        entryUrl,
        isTimingAllowed = false,
        modalID,
        links = [],
        playlistId,
        analyticsData,
        customShareItems = [],
    } = props;

    // States for fetched data when playlistId exists but embed settings aren't provided
    const [fetchedData, setFetchedData] = useState<{
        embedSettings: EmbedSettings[] | null;
        availableFeatures: EmbedSettingsAvailableFeatures | null;
        embedCodeParams: EmbedCodeProps | null;
        playerConfig: PlayerConfig | null;
    } | null>(null);

    // Use either prop values or fetched values
    const embedSettings = propEmbedSettings || fetchedData?.embedSettings || null;
    const availableFeatures = propAvailableFeatures || fetchedData?.availableFeatures || undefined;
    const embedCodeParams = propEmbedCodeParams || fetchedData?.embedCodeParams || null;
    const playerConfig = propPlayerConfig || fetchedData?.playerConfig || null;

    const { showToast } = useToastsContext();

    // Fetch data if playlistId exists but embed settings aren't provided
    useEffect(() => {
        const fetchPlaylistEmbedData = async () => {
            // Only fetch if:
            // 1. Modal is open
            // 2. We have a playlistId
            // 3. We need at least one of the required props and haven't fetched data yet
            const needsToFetch = open && !!playlistId && (!embedSettings || !embedCodeParams || !playerConfig);

            if (needsToFetch) {
                try {
                    const response = await postKmsData(
                        "/default/playlists-ds/get-playlist-embed-data",
                        {
                            playlistId,
                        }
                    );

                    if (response && response.success) {
                        // Store all fetched data in a single state update
                        setFetchedData({
                            embedSettings: response.embedSettings || null,
                            availableFeatures: response.availableFeatures || null,
                            embedCodeParams: response.embedCodeParams || null,
                            playerConfig: response.playerConfig || null,
                        });
                    }
                    else {
                        showToast({ severity: "error", message: response.errorMessage });
                    }
                }
                catch {
                    showToast({ severity: "error", message: translate("Error fetching playlist embed data") });
                }
            }
        };

        fetchPlaylistEmbedData();
    }, [embedCodeParams, embedSettings, open, playerConfig, playlistId, showToast]);

    const hasAllSettings = !!(embedSettings && embedCodeParams && playerConfig);
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;
    const initialShowEmbed = isKaf ? hasAllSettings : false;

    const [showEmbed, setShowEmbed] = useState<boolean>(initialShowEmbed);
    const [showOEmbed, setShowOEmbed] = useState<boolean>(false);
    const [showCustomShare, setShowCustomShare] = useState(false);
    const [selectedCustomShare, setSelectedCustomShare] = useState<KmsTypeComponent<any, { onClose: () => void }>>();

    const sendButtonAnalytics = useButtonAnalytics();

    const onCustomShareItemSelected = (component: KmsTypeComponent<any, { onClose: () => void }>) => {
        setSelectedCustomShare(component);
        setShowCustomShare(true);
    };

    const handleClose = (analyticsButtonName?: string) => {
        if (analyticsButtonName) {
            sendButtonAnalytics(`Close ${analyticsButtonName} modal`, ButtonClickAnalyticsType.CLOSE);
        }

        setTimeout(
            () => {
                // setTimeout prevents flickering of the initial view before modal close
                setShowEmbed(initialShowEmbed);
                setShowOEmbed(false);

                // Reset fetched data when modal closes to allow fetching again when reopened
                setFetchedData(null);
            },
            500
        );

        onClose();
    };

    const v7Players = embedSettings && embedSettings?.filter((player) => player.isPlayerV7);
    const showNoPlayers = v7Players?.length === 0;
    // no modal close button for embed/oembed modal or no players message modal
    let onCloseAction: (() => void) | undefined = handleClose;
    if ((showEmbed || showOEmbed) && !showNoPlayers) {
        onCloseAction = undefined;
    }
    // set modal title text
    let modalTitleText = translate("Share");
    if (showEmbed) {
        modalTitleText = translate("Embed");
    }
    else if (showOEmbed) {
        modalTitleText = translate("oEmbed link");
    }

    const showEmbeds = !!(embedSettings && embedSettings.length > 0);

    return !showCustomShare ? (
        <StyledModal
            id={modalID}
            open={open}
            onClose={onCloseAction}
            showEmbed={showEmbed || showOEmbed}
            showNoPlayers={showNoPlayers}
            className="kms-ds-share-modal"
            onKeyDown={(ev) => ev.stopPropagation()}
        >
            <ModalTitle closeButtonAriaLabel={translate("Close %1 modal", [modalTitleText])} onClose={onCloseAction}>
                {!showNoPlayers && modalTitleText}
            </ModalTitle>

            {!showEmbed && !showOEmbed && (
                <ButtonAnalyticsProvider location={`Modal - Share`}>
                    <StyledModalContent>
                        <ShareSection
                            title={title}
                            setShowEmbed={setShowEmbed}
                            setShowOEmbed={setShowOEmbed}
                            showEmbeds={showEmbeds}
                            hideEmbed={hideEmbed}
                            oEmbed={oEmbed}
                            entryUrl={entryUrl}
                            isTimingAllowed={isTimingAllowed}
                            links={links}
                            copyToClipboardText={
                                playlistId ? translate("Link to playlist page") : translate("Link to media page")
                            }
                            analyticsData={analyticsData}
                            customShareItems={customShareItems}
                            customShareSelected={onCustomShareItemSelected}
                        />
                    </StyledModalContent>
                </ButtonAnalyticsProvider>
            )}

            {(showEmbed || showOEmbed) && embedSettings && embedCodeParams && playerConfig && (
                <ButtonAnalyticsProvider location={`Modal - Embed`}>
                    <SettingsContainer
                        embedSettings={embedSettings}
                        availableFeatures={availableFeatures}
                        embedCodeParams={embedCodeParams}
                        playerConfig={playerConfig}
                        onClose={handleClose}
                        embedMethod={showEmbed ? "embed" : "oembed"}
                        allowOembedConfig={showOEmbed ? allowOembedConfig : undefined}
                        isTimingAllowed={isTimingAllowed}
                    />
                </ButtonAnalyticsProvider>
            )}
        </StyledModal>
    ) : (
        selectedCustomShare && (
            <CustomComponent component={selectedCustomShare} onClose={() => setShowCustomShare(false)} />
        )
    );
};

export default ShareModal;
